import { useState, useEffect, useRef } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { db } from '../../config/firebaseConfig';
import { useTranslation } from '../../i18n/LanguageContext';
import { findRegionByCity, getResponsibleManagerIds } from './regionUtils';
import { CENTER_COLUMNS } from './centerColumns';

/** Shared by this hook and the center form, so a save shows one "updated" toast. */
export const CENTER_UPDATED_TOAST = 'center-updated';

/**
 * Merkez Yönetimi'nin veri mantığı: merkez, çalışan ve bölge okuma, süzgeçler,
 * durum değiştirme, silme (projelerdeki çalışanlara SMO bilgisi yazılarak) ve
 * Excel çıktısı (Türkçe). Eski sayfadaki kod birebir taşındı; bildirimler çevrildi.
 */
const useCenters = () => {
  const { t } = useTranslation();
  // Toasts read the current language through a ref, so switching it never refetches
  const labelRef = useRef(null);
  labelRef.current = (key, vars) => t(`centers.${key}`, vars);
  const msg = (key, vars) => labelRef.current(`toast.${key}`, vars);
  const [centers, setCenters] = useState([]);
  const [filteredCenters, setFilteredCenters] = useState([]);
  const [employeesMap, setEmployeesMap] = useState({});
  const [filters, setFilters] = useState({ 'Durum': 'Aktif' });
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSmoOverviewOpen, setIsSmoOverviewOpen] = useState(false);
  const [isRegionModalOpen, setIsRegionModalOpen] = useState(false);
  const [regions, setRegions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  // Excel başlıkları eski sütun etiketleriyle (Türkçe) kalır
  const columns = CENTER_COLUMNS;

  const fetchCenters = async () => {
    setIsLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'Centers'));
      const centersData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCenters(centersData);
      setFilteredCenters(centersData);
    } catch (error) {
      console.error('Error fetching centers:', error);
      toast.error(msg('loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'Employees'));
      const map = {};
      snapshot.docs.forEach(doc => {
        const d = doc.data();
        map[doc.id] = `${d.eName || ''} ${d.eSurname || ''}`.trim();
      });
      setEmployeesMap(map);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const getSMOName = (center) => {
    const raw = center?.SMO;
    if (!raw) return '';
    return employeesMap[raw] || raw;
  };

  const getSMOBackupName = (center) => {
    const raw = center?.SMOBackup;
    if (!raw) return '';
    return employeesMap[raw] || raw;
  };

  const fetchRegions = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'Regions'));
      setRegions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  };

  const getRegionForCenter = (center) => findRegionByCity(regions, center?.['İl']);

  const getRegionManagerName = (center) => {
    const region = getRegionForCenter(center);
    if (!region) return '';
    return getResponsibleManagerIds(region, center?.['İl'])
      .map((managerId) => employeesMap[managerId])
      .filter(Boolean)
      .join(', ');
  };

  useEffect(() => {
    fetchCenters();
    fetchEmployees();
    fetchRegions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let filtered = centers;

    // Apply search term first
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        center => 
          (center['Adı'] && center['Adı'].toString().toLowerCase().includes(lowercasedSearch)) ||
          (center['İl'] && center['İl'].toString().toLowerCase().includes(lowercasedSearch)) ||
          (center['İlçe'] && center['İlçe'].toString().toLowerCase().includes(lowercasedSearch))
      );
    }

    // Then apply individual column filters
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        if (key === 'Durum') {
          filtered = filtered.filter(center => center[key] === filters[key]);
        } else if (key === 'SMO') {
          filtered = filtered.filter(center =>
            getSMOName(center)
              .toLowerCase()
              .includes(filters[key].toLowerCase())
          );
        } else if (key === 'Yedek SMO') {
          filtered = filtered.filter(center =>
            getSMOBackupName(center)
              .toLowerCase()
              .includes(filters[key].toLowerCase())
          );
        } else if (key === 'Bölge Müdürü') {
          filtered = filtered.filter(center =>
            getRegionManagerName(center)
              .toLowerCase()
              .includes(filters[key].toLowerCase())
          );
        } else {
          filtered = filtered.filter(center =>
            String(center[key])
              ?.toLowerCase()
              .includes(filters[key].toLowerCase())
          );
        }
      }
    });

    setFilteredCenters(filtered);
  }, [centers, filters, searchTerm, regions, employeesMap]);

  const handleFilter = (column, value) => {
    const newFilters = { ...filters, [column]: value };
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const activeCount = centers.filter(center => center['Durum'] === 'Aktif').length;
  const inactiveCount = centers.filter(center => center['Durum'] === 'İnaktif').length;

  // Görünen sütunlar ekrandaki sütun menüsünden gelir ({ anahtar: true/false }, eski sırayla)
  const downloadExcel = (visibleColumns) => {
    try {
      const dataToExport = centers.map(center => {
        const exportData = {};
        Object.keys(visibleColumns).forEach(key => {
          if (visibleColumns[key]) {
            if (key === 'SMO') {
              exportData[columns[key].label] = getSMOName(center);
            } else if (key === 'Yedek SMO') {
              exportData[columns[key].label] = getSMOBackupName(center);
            } else if (key === 'Bölge Müdürü') {
              exportData[columns[key].label] = getRegionManagerName(center);
            } else {
              exportData[columns[key].label] = center[key] || '';
            }
          }
        });
        return exportData;
      });

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Merkezler');
      XLSX.writeFile(workbook, 'MerkezListesi.xlsx');
      
      toast.success(msg('excelDone'));
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      toast.error(msg('excelError'));
    }
  };

  const handleCenterSelect = (center) => {
    setSelectedCenter(center);
  };

  const handleStatusToggle = async (center = null) => {
    const targetCenter = center || selectedCenter;
    if (!targetCenter) return;
    
    const newStatus = targetCenter['Durum'] === 'Aktif' ? 'İnaktif' : 'Aktif';
    
    try {
      const centerRef = doc(db, 'Centers', targetCenter.id);
      await updateDoc(centerRef, { 'Durum': newStatus });
      
      // Update the selectedCenter state with the new status
      setSelectedCenter(prev => ({
        ...prev,
        'Durum': newStatus
      }));
      
      // Update the centers and filteredCenters states
      setCenters(prev => prev.map(c => 
        c.id === targetCenter.id ? { ...c, 'Durum': newStatus } : c
      ));
      setFilteredCenters(prev => prev.map(c => 
        c.id === targetCenter.id ? { ...c, 'Durum': newStatus } : c
      ));
      
      toast.success(msg('statusUpdated', { status: labelRef.current(newStatus === 'Aktif' ? 'status.active' : 'status.inactive') }));
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(msg('statusError'));
    }
  };

  const [centerToDelete, setCenterToDelete] = useState(null);

  const openDeleteModal = async (center) => {
    // Check if center is used in any projects
    try {
      const projectsSnapshot = await getDocs(collection(db, 'Projects'));
      let isUsedInProjects = false;
      
      for (const projectDoc of projectsSnapshot.docs) {
        const projectData = projectDoc.data();
        const employees = projectData.employees || [];
        
        // Check if any employee is assigned to this center
        if (employees.some(emp => emp.centerId === center.id)) {
          isUsedInProjects = true;
          break;
        }
      }
      
      setCenterToDelete({ ...center, isUsedInProjects });
      setIsDeleteModalOpen(true);
    } catch (error) {
      console.error('Error checking center usage:', error);
      toast.error(msg('checkError'));
    }
  };

  const handleDeleteCenter = async () => {
    if (!centerToDelete) return;
    
    try {
      console.log('🗑️ Deleting center:', centerToDelete.id, centerToDelete.Adı);
      console.log('📋 Center SMO:', centerToDelete.SMO);
      
      // Before deleting, add SMO info to all employees in Projects collection that use this center
      const projectsSnapshot = await getDocs(collection(db, 'Projects'));
      let updatedProjectsCount = 0;
      let updatedEmployeesCount = 0;
      
      for (const projectDoc of projectsSnapshot.docs) {
        const projectData = projectDoc.data();
        const employees = projectData.employees || [];
        
        // Find employees using this center
        let hasChanges = false;
        const updatedEmployeesList = employees.map(emp => {
          if (emp.centerId === centerToDelete.id) {
            // Add SMO info if not already present
            if (!emp.smoId && centerToDelete.SMO) {
              hasChanges = true;
              updatedEmployeesCount++;
              console.log(`✅ Adding SMO ${centerToDelete.SMO} to employee ${emp.name} ${emp.surname} in project ${projectDoc.id}`);
              return {
                ...emp,
                smoId: centerToDelete.SMO,
                centerDeletedAt: new Date().toISOString() // Mark when center was deleted
              };
            }
          }
          return emp;
        });
        
        // Update project if there were changes
        if (hasChanges) {
          await updateDoc(doc(db, 'Projects', projectDoc.id), {
            employees: updatedEmployeesList
          });
          updatedProjectsCount++;
        }
      }
      
      console.log(`📊 Updated ${updatedEmployeesCount} employees in ${updatedProjectsCount} projects`);
      
      // Now delete the center
      await deleteDoc(doc(db, 'Centers', centerToDelete.id));
      
      toast.success(msg('deleted', { count: updatedEmployeesCount }));
      setIsDeleteModalOpen(false);
      setCenterToDelete(null);
      fetchCenters();
    } catch (error) {
      console.error('Error deleting center:', error);
      toast.error(msg('deleteError'));
    }
  };

  const handleAddCenter = async (centerData) => {
    try {
      console.log('New center data:', centerData);
      setIsAddModalOpen(false);
      await fetchCenters();
      toast.success(msg('added'));
    } catch (error) {
      console.error('Error adding center:', error);
      toast.error(msg('addError'));
    }
  };

  const handleEditCenter = (center) => {
    setSelectedCenter(center);
    setIsEditModalOpen(true);
  };

  const handleCenterUpdate = () => {
    setIsEditModalOpen(false);
    fetchCenters();
    toast.success(msg('updated'), { toastId: CENTER_UPDATED_TOAST });
  };

  return {
    isLoading, centers, filteredCenters, employeesMap, regions,
    filters, handleFilter, clearFilters, searchTerm, setSearchTerm,
    activeCount, inactiveCount,
    getSMOName, getSMOBackupName, getRegionForCenter, getRegionManagerName,
    selectedCenter, setSelectedCenter, handleCenterSelect,
    isAddModalOpen, setIsAddModalOpen, isEditModalOpen, setIsEditModalOpen,
    isDeleteModalOpen, setIsDeleteModalOpen, isSmoOverviewOpen, setIsSmoOverviewOpen,
    isRegionModalOpen, setIsRegionModalOpen,
    centerToDelete, setCenterToDelete, openDeleteModal, handleDeleteCenter,
    handleStatusToggle, handleAddCenter, handleEditCenter, handleCenterUpdate,
    fetchCenters, fetchRegions, downloadExcel,
  };
};

export default useCenters;
